import { CommunityPriceRow, ContextLabel, HourlyBaseTariff, HourlyFee, HourlyTariffData, HourlyTariffWindow, LocationTariff, PriceComponent, PriceHistoryRow, Restrictions, Tariff } from "../types";
import { formatCurrency, formatHoursHHmm } from "./format";

export const priceWithVAT = (pc?: PriceComponent): number | null => {
    if (!pc || pc.price == null) return null;
    const vatPct = pc.vat ?? 0;
    return pc.price * (1 + vatPct / 100);
};

export const findElement = (tariff: Tariff | undefined, label: ContextLabel, predicate?: (e: Tariff["elements"][number]) => boolean) => {
    return tariff?.elements?.find(e => e.context_label === label && (predicate ? predicate(e) : true));
};

export const pickComponent = (el: Tariff["elements"][number] | undefined, type?: PriceComponent["type"]) => {
    if (!el) return undefined;
    return type ? el.price_components.find(pc => pc.type === type) : el.price_components[0];
};

export const hoursFrom = (seconds?: number) => (seconds == null ? null : seconds / 3600);

// High-level getters (reusable across app)
export const getEnergyUnitPriceGross = (tariff: Tariff): number | null => {
    const el = findElement(tariff, "ENERGY_PRICE");
    const pc = pickComponent(el, "ENERGY") ?? pickComponent(el);
    return priceWithVAT(pc);
};

export const getBlockingFeePerUnitGross = (tariff: Tariff): number | null => {
    const el = findElement(tariff, "BLOCKING_FEE");
    const pc = pickComponent(el, "TIME") ?? pickComponent(el);
    return priceWithVAT(pc);
};

export const getBlockingFeeStartsAfterHrs = (tariff: Tariff): number | null => {
    const el = findElement(tariff, "BLOCKING_FEE")
        ?? findElement(tariff, "BLOCKING_FEE_DAY")
        ?? findElement(tariff, "BLOCKING_FEE_NIGHT");
    return hoursFrom(el?.restrictions?.min_duration);
};

export const getBlockingFeeCapGross = (tariff: Tariff): number | null => {
    const el = findElement(tariff, "BLOCKING_FEE_CAP");
    let pc = pickComponent(el, "FLAT") ?? pickComponent(el);

    if (pc === undefined) {
        const el = findElement(tariff, "BLOCKING_FEE", e => e.restrictions?.max_duration == null);
        pc = pickComponent(el, "FLAT") ?? pickComponent(el);
    }

    return priceWithVAT(pc);
};

export const getBlockingFeeCapStartsAfterHrs = (tariff: Tariff): number | null => {
    let el = findElement(tariff, "BLOCKING_FEE_CAP")
        ?? findElement(tariff, "BLOCKING_FEE_CAP_DAY")
        ?? findElement(tariff, "BLOCKING_FEE_CAP_NIGHT");
    if (el === undefined)
        el = findElement(tariff, "BLOCKING_FEE", e => e.restrictions?.max_duration == null)
            ?? findElement(tariff, "BLOCKING_FEE_DAY", e => e.restrictions?.max_duration == null)
            ?? findElement(tariff, "BLOCKING_FEE_NIGHT", e => e.restrictions?.max_duration == null);
    return hoursFrom(el?.restrictions?.min_duration);
};

const getFeeAfterHrsByLabel = (tariff: Tariff, feeLabel: ContextLabel): number | null => {
    const el = findElement(tariff, feeLabel);
    return hoursFrom(el?.restrictions?.min_duration);
};

const getCapAfterHrsByLabel = (tariff: Tariff, feeLabel: ContextLabel, capLabel: ContextLabel): number | null => {
    const el = findElement(tariff, capLabel)
        ?? findElement(tariff, feeLabel, e => e.restrictions?.max_duration == null);
    return hoursFrom(el?.restrictions?.min_duration);
};

const getPriceByLabel = (tariff: Tariff, label: ContextLabel, pcType: PriceComponent["type"]): number | null => {
    const el = findElement(tariff, label);
    const pc = pickComponent(el, pcType) ?? pickComponent(el);
    return priceWithVAT(pc);
};

// ─── Hourly tariff mapping ────────────────────────────────────────────────────

const windowKey = (r: Restrictions): string =>
    `${r.start_time ?? ""}|${r.end_time ?? ""}|${(r.day_of_week ?? []).slice().sort().join(",")}`;

const buildHourlyFee = (
    tariff: Tariff,
    label: ContextLabel,
    pcType: PriceComponent["type"],
    predicate?: (e: Tariff["elements"][number]) => boolean,
): HourlyFee => {
    const el = findElement(tariff, label, predicate);
    const pc = pickComponent(el, pcType) ?? pickComponent(el);
    const price = priceWithVAT(pc);
    return {
        price: formatCurrency(price, tariff.currency),
        unit: tariff.currency,
        minDuration: formatHoursHHmm(hoursFrom(el?.restrictions?.min_duration)) === "—"
            ? null
            : formatHoursHHmm(hoursFrom(el?.restrictions?.min_duration)),
    };
};

const buildWindowFees = (tariff: Tariff, key: string): Omit<HourlyTariffWindow, "startTime" | "endTime" | "weekdays"> => {
    const matchKey = (e: Tariff["elements"][number]) =>
        !!e.restrictions && windowKey(e.restrictions) === key;

    const energyEl = tariff.elements.find(e => e.context_label === "ENERGY_PRICE" && matchKey(e));
    const blockingEl = tariff.elements.find(e => e.context_label === "BLOCKING_FEE" && matchKey(e));
    const capEl = tariff.elements.find(e => e.context_label === "BLOCKING_FEE_CAP" && matchKey(e));
    const activationEl = tariff.elements.find(e => e.context_label === "ACTIVATION" && matchKey(e));

    const toFee = (el: Tariff["elements"][number] | undefined, pcType: PriceComponent["type"]): HourlyFee => {
        const pc = pickComponent(el, pcType) ?? pickComponent(el);
        const price = priceWithVAT(pc);
        const minDur = formatHoursHHmm(hoursFrom(el?.restrictions?.min_duration));
        return {
            price: formatCurrency(price, tariff.currency),
            unit: tariff.currency,
            minDuration: minDur === "—" ? null : minDur,
        };
    };

    return {
        energyFee: toFee(energyEl, "ENERGY"),
        blockingFee: toFee(blockingEl, "TIME"),
        blockingFeeCap: toFee(capEl, "FLAT"),
        activationFee: toFee(activationEl, "FLAT"),
    };
};

export const mapToHourlyTariff = (tariff: Tariff): HourlyTariffData => {
    // Separate elements with time restrictions (windows) from those without (base)
    const windowKeys = new Map<string, Restrictions>();
    for (const el of tariff.elements) {
        if (el.restrictions?.start_time || el.restrictions?.end_time || el.restrictions?.day_of_week?.length) {
            const key = windowKey(el.restrictions!);
            if (!windowKeys.has(key)) windowKeys.set(key, el.restrictions!);
        }
    }

    const tariffWindows: HourlyTariffWindow[] = Array.from(windowKeys.entries()).map(([key, r]) => ({
        startTime: r.start_time ?? "",
        endTime: r.end_time ?? "",
        weekdays: r.day_of_week ?? [],
        ...buildWindowFees(tariff, key),
    }));

    // Base tariff: elements without time restrictions
    const baseTariffProxy: Tariff = {
        ...tariff,
        elements: tariff.elements.filter(el =>
            !el.restrictions?.start_time && !el.restrictions?.end_time && !el.restrictions?.day_of_week?.length
        ),
    };

    const baseTariff: HourlyBaseTariff = {
        energyFee: buildHourlyFee(baseTariffProxy, "ENERGY_PRICE", "ENERGY"),
        blockingFee: buildHourlyFee(baseTariffProxy, "BLOCKING_FEE", "TIME"),
        blockingFeeCap: buildHourlyFee(baseTariffProxy, "BLOCKING_FEE_CAP", "FLAT"),
        activationFee: buildHourlyFee(baseTariffProxy, "ACTIVATION", "FLAT"),
    };

    return { tariffWindows, baseTariff };
};

// ─────────────────────────────────────────────────────────────────────────────

export const buildCommunityPriceRow = (input: LocationTariff): CommunityPriceRow => {
    const { location_id, tariff } = input;
    const altText = tariff.tariff_alt_text?.[0]?.text;
    const isDayNight = altText === "DAY_NIGHT";
    const isHourly = altText === "HOURLY";
    const base: CommunityPriceRow = {
        location_id,
        tariff_id: tariff.id,
        tariff_type: isDayNight ? "DAY_NIGHT" : isHourly ? "HOURLY" : (tariff.type ?? "REGULAR"),
        energy_price: formatCurrency(getEnergyUnitPriceGross(tariff), tariff.currency),
        blocking_fee: formatCurrency(getBlockingFeePerUnitGross(tariff), tariff.currency),
        blocking_fee_after_hours: formatHoursHHmm(getBlockingFeeStartsAfterHrs(tariff)),
        blocking_fee_cap: formatCurrency(getBlockingFeeCapGross(tariff), tariff.currency),
        blocking_fee_cap_after_hours: formatHoursHHmm(getBlockingFeeCapStartsAfterHrs(tariff)),
    };
    if (isDayNight) {
        base.energy_price_day = formatCurrency(getPriceByLabel(tariff, "ENERGY_PRICE_DAY", "ENERGY"), tariff.currency);
        base.energy_price_night = formatCurrency(getPriceByLabel(tariff, "ENERGY_PRICE_NIGHT", "ENERGY"), tariff.currency);
        base.blocking_fee_day = formatCurrency(getPriceByLabel(tariff, "BLOCKING_FEE_DAY", "TIME"), tariff.currency);
        base.blocking_fee_night = formatCurrency(getPriceByLabel(tariff, "BLOCKING_FEE_NIGHT", "TIME"), tariff.currency);
        base.blocking_fee_after_hours_day = formatHoursHHmm(getFeeAfterHrsByLabel(tariff, "BLOCKING_FEE_DAY"));
        base.blocking_fee_after_hours_night = formatHoursHHmm(getFeeAfterHrsByLabel(tariff, "BLOCKING_FEE_NIGHT"));
        base.blocking_fee_cap_day = formatCurrency(getPriceByLabel(tariff, "BLOCKING_FEE_CAP_DAY", "FLAT"), tariff.currency);
        base.blocking_fee_cap_night = formatCurrency(getPriceByLabel(tariff, "BLOCKING_FEE_CAP_NIGHT", "FLAT"), tariff.currency);
        base.blocking_fee_cap_after_hours_day = formatHoursHHmm(getCapAfterHrsByLabel(tariff, "BLOCKING_FEE_DAY", "BLOCKING_FEE_CAP_DAY"));
        base.blocking_fee_cap_after_hours_night = formatHoursHHmm(getCapAfterHrsByLabel(tariff, "BLOCKING_FEE_NIGHT", "BLOCKING_FEE_CAP_NIGHT"));
    }
    if (isHourly) {
        const hourlyData = mapToHourlyTariff(tariff);
        base.hourlyData = hourlyData;
        base.energy_price = hourlyData.baseTariff.energyFee.price;
        base.blocking_fee = hourlyData.baseTariff.blockingFee.price;
        base.blocking_fee_after_hours = hourlyData.baseTariff.blockingFee.minDuration ?? "—";
        base.blocking_fee_cap = hourlyData.baseTariff.blockingFeeCap.price;
        base.blocking_fee_cap_after_hours = hourlyData.baseTariff.blockingFeeCap.minDuration ?? "—";
    }
    return base;
};

export const buildPriceHistoryRow = (tariff: Tariff): PriceHistoryRow => {
    const altText = tariff.tariff_alt_text?.[0]?.text;
    const isDayNight = altText === "DAY_NIGHT";
    const isHourly = altText === "HOURLY";
    const base: PriceHistoryRow = {
        tariff_id: tariff.id,
        tariff_type: isDayNight ? "DAY_NIGHT" : isHourly ? "HOURLY" : (tariff.type ?? "REGULAR"),
        energy_price: formatCurrency(getEnergyUnitPriceGross(tariff), tariff.currency),
        blocking_fee: formatCurrency(getBlockingFeePerUnitGross(tariff), tariff.currency),
        blocking_fee_after_hours: formatHoursHHmm(getBlockingFeeStartsAfterHrs(tariff)),
        blocking_fee_cap: formatCurrency(getBlockingFeeCapGross(tariff), tariff.currency),
        blocking_fee_cap_after_hours: formatHoursHHmm(getBlockingFeeCapStartsAfterHrs(tariff)),
        active_from: tariff.start_date_time,
        active_till: tariff.end_date_time,
    };
    if (isDayNight) {
        base.energy_price_day = formatCurrency(getPriceByLabel(tariff, "ENERGY_PRICE_DAY", "ENERGY"), tariff.currency);
        base.energy_price_night = formatCurrency(getPriceByLabel(tariff, "ENERGY_PRICE_NIGHT", "ENERGY"), tariff.currency);
        base.blocking_fee_day = formatCurrency(getPriceByLabel(tariff, "BLOCKING_FEE_DAY", "TIME"), tariff.currency);
        base.blocking_fee_night = formatCurrency(getPriceByLabel(tariff, "BLOCKING_FEE_NIGHT", "TIME"), tariff.currency);
        base.blocking_fee_after_hours_day = formatHoursHHmm(getFeeAfterHrsByLabel(tariff, "BLOCKING_FEE_DAY"));
        base.blocking_fee_after_hours_night = formatHoursHHmm(getFeeAfterHrsByLabel(tariff, "BLOCKING_FEE_NIGHT"));
        base.blocking_fee_cap_day = formatCurrency(getPriceByLabel(tariff, "BLOCKING_FEE_CAP_DAY", "FLAT"), tariff.currency);
        base.blocking_fee_cap_night = formatCurrency(getPriceByLabel(tariff, "BLOCKING_FEE_CAP_NIGHT", "FLAT"), tariff.currency);
        base.blocking_fee_cap_after_hours_day = formatHoursHHmm(getCapAfterHrsByLabel(tariff, "BLOCKING_FEE_DAY", "BLOCKING_FEE_CAP_DAY"));
        base.blocking_fee_cap_after_hours_night = formatHoursHHmm(getCapAfterHrsByLabel(tariff, "BLOCKING_FEE_NIGHT", "BLOCKING_FEE_CAP_NIGHT"));
    }
    if (isHourly) {
        base.hourlyData = mapToHourlyTariff(tariff);
    }
    return base;
};

export const mapToCommunityPriceRows = (data: LocationTariff[]): CommunityPriceRow[] =>
    data.map(buildCommunityPriceRow);

export const mapAndSortPriceHistory = (data: Tariff[]): PriceHistoryRow[] => {
    const rows = data.map(buildPriceHistoryRow)
    return rows.sort((a, b) => new Date(b.active_from ?? 0).getTime() - new Date(a.active_from ?? 0).getTime());
}
